import { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import DeleteIcon from '@mui/icons-material/Delete';
import { Box, Card, CardContent, Collapse, IconButton, styled, Typography } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { TFormType } from 'src/@types';
import CEditor from 'src/components/editor/CEditor';
import { RHFTextField } from 'src/components/hook-form';
import RHFUploadImage from 'src/components/hook-form/uploadImage/RHFUploadImage';
import { FormInnerContainer, FormItem, FormLabel, FormRow } from 'src/layouts/form/FormLayout';
import { FormType } from 'src/static/constants/common/form';

interface ISubContentFormProps {
  formType: TFormType;
  index: number;
  onDelete?: () => void;
}

export const SubContentForm = observer(({ formType, index, onDelete }: ISubContentFormProps) => {
  const { t } = useTranslation();
  const { getValues, setValue } = useFormContext();
  const readonly = formType === FormType.READONLY || getValues('delYn');
  // 컨텐츠 폴딩 여부
  const [expanded, setExpanded] = useState(true);
  const handleToggleExpand = () => {
    setExpanded((prev) => !prev);
  };

  // 데스크탑 이미지
  const [desktopImageFiles, setDesktopImageFiles] = useState<Array<File | string>>([]);
  // 모바일 이미지
  const [mobileImageFiles, setMobileImageFiles] = useState<Array<File | string>>([]);

  useEffect(() => {
    setDesktopImageFiles(getValues(`subContentInfos[${index}].desktopImagePath`));
    setMobileImageFiles(getValues(`subContentInfos[${index}].mobileImagePath`));
  }, []);

  return (
    <Card sx={{ mb: 3, border: '1px solid #ddd', borderRadius: 2, overflow: 'hidden' }}>
      <CardHeader>
        <Typography variant="h6" fontWeight="bold">
          {t('business.form.subContent')} {index + 1}
        </Typography>

        <Box>
          <IconButton onClick={handleToggleExpand} sx={{ color: 'grey.700' }}>
            <ExpandMoreIcon
              sx={{
                transform: expanded ? 'rotate(0deg)' : 'rotate(180deg)',
                transition: 'transform 0.3s',
              }}
            />
          </IconButton>
          <IconButton onClick={onDelete} sx={{ color: 'grey.700' }}>
            <DeleteIcon />
          </IconButton>
        </Box>
      </CardHeader>

      {/* 카드 내용 */}
      <Collapse in={expanded}>
        <CardContent>
          <FormRow sx={{ gridTemplateColumns: '1fr' }}>
            <FormItem>
              <RHFTextField
                label={t('business.form.subContentTitle')}
                name={`subContentInfos[${index}].title`}
                disabled={readonly}
              />
            </FormItem>
            <FormItem>
              <CEditor
                name={`subContentInfos[${index}].descript`}
                placeholder={t('business.form.subContentDesc')}
              />
            </FormItem>
          </FormRow>
          <FormInnerContainer>
            <FormLabel>{t('business.form.desktopImage')}</FormLabel>
            <FormRow sx={{ gridTemplateColumns: '1fr' }}>
              <FormItem>
                <RHFUploadImage
                  name="desktopImage"
                  files={desktopImageFiles}
                  onChange={(newFile: Array<File | string>) => {
                    setDesktopImageFiles(newFile);
                    setValue(`subContentInfos[${index}].desktopImagePath`, newFile);
                  }}
                />
              </FormItem>
            </FormRow>
          </FormInnerContainer>

          <FormInnerContainer>
            <FormLabel>{t('business.form.mobileImage')}</FormLabel>
            <FormRow sx={{ gridTemplateColumns: '1fr' }}>
              <FormItem>
                <RHFUploadImage
                  name="mobileImage"
                  files={mobileImageFiles}
                  onChange={(newFile: Array<File | string>) => {
                    setMobileImageFiles(newFile);
                    setValue(`subContentInfos[${index}].mobileImagePath`, newFile);
                  }}
                />
              </FormItem>
            </FormRow>
          </FormInnerContainer>
        </CardContent>
      </Collapse>
    </Card>
  );
});

const CardHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '16px',
  backgroundColor: '#f5f5f5',
  borderBottom: '1px solid #ddd',
}));
